import {common, priority, wcfclient} from '@priority-software/priority-proto-api/lib';

import {getParentValueByColName, getValueByColName} from '../../_shared/GWTBridge/invoke/formTable';
import {getFormName, getParentFormName} from '../../_shared/GWTBridge/invoke/recon';
import {getAdditionalProps} from '../../services/proto/core/loginProps';
import {callServer} from '../../services/proto/core/server';
import {ServerMethods} from '../../services/proto/core/constants';
import {endForm, startForm} from '../../services/form/form';
import {getServerStoreData, setServerStoreData} from '../../services/proto/ServerStorage/service';
import {
    COL_NAME_APP_FIELDS_NAME,
    COL_NAME_APP_FIELDS_PREVIEW,
    COL_NAME_APP_FIELDS_TITLE,
    COL_NAME_APP_LANG,
    COL_NAME_APP_NAME,
    COL_NAME_KLINE,
    COL_NAME_SCREEN_NAME,
    COL_NAME_TYPE
} from './consts';
import {IQueryFilter} from './types/filters';
import {ISearchGeneral, ISearchQuery, KEY_PREFIX, SEARCH_INIT} from './types/search';
import {SelectedFields, serializeRecords} from './utils';

export const getFormData = () => {
    const appName = getParentValueByColName(COL_NAME_APP_NAME);
    const lang = getParentValueByColName(COL_NAME_APP_LANG);
    const screenName = getValueByColName(COL_NAME_SCREEN_NAME);
    const screenKey = getValueByColName(COL_NAME_KLINE);
    return {appName, screenName, screenKey, lang};
};

const buildKey = () => {
    const {appName, screenName, screenKey, lang} = getFormData();
    return `${KEY_PREFIX}_${appName}_${screenName}_${screenKey}_${lang}`;
};
export const fetchScreenSearch = async (): Promise<{
    general: ISearchGeneral;
    filters: IQueryFilter[];
}> => {
    const key = buildKey();
    const data = await getServerStoreData([key], false, false);
    const dataString = data[key]?.Str;
    if (!dataString) {
        return {general: SEARCH_INIT, filters: []};
    }

    const searchQuery = JSON.parse(dataString) as ISearchQuery;

    return {
        general: searchQuery.general,
        filters: searchQuery.filters.map(filter => ({
            ...filter,
            Condition: Array.isArray(filter.Condition) ? filter.Condition : [filter.Condition] //support old searches
        }))
    };
};

export const saveSearch = async (search: ISearchQuery) => {
    const key = buildKey();
    console.log('appsearch', search); //keep for eva testing
    const isSearchEmpty = !search.filters?.length && !search.general.name;
    const searchToSave = isSearchEmpty
        ? new common.Constant({Str: ''})
        : new common.Constant({Str: JSON.stringify(search)});
    const appSearch = {[key]: searchToSave};

    return setServerStoreData(appSearch, false, false);
};

export const fetchFields = async (): Promise<SelectedFields[]> => {
    const {rows: parentFormRows, form} = await fetchParentFormRows();
    const Session = form?.Session;

    if (!Session) {
        throw new Error('error');
    }

    const subFormRows =
        parentFormRows && (await fetchSubFormRows(Session, parentFormRows.Records[0].Fields));
    const fields = subFormRows && (await fetchFormFields(Session, subFormRows.Records[0].Fields));

    parentFormRows && (await endForm(Session));
    subFormRows && (await endForm(Session));
    subFormRows && (await endForm(Session));

    if (!fields?.Records) {
        throw new Error('error');
    }

    return serializeRecords(fields?.Records);
};

const fetchParentFormRows = async () => {
    const parentName = getParentFormName();
    const {appName} = getFormData();
    let Session;
    try {
        const form = await startForm(parentName);
        Session = form.Session;
        const Properties = getAdditionalProps();

        const columns: priority.netitems.form.ColumnsRs = await callServer(
            wcfclient.columns,
            new priority.netitems.form.ColumnsRq({
                Session: Session,
                Properties: getAdditionalProps()
            }),
            ServerMethods.ColumnsRq
        );

        if (!Session) {
            throw new Error('error');
        }

        const appNameColId = columns.Columns.find(col => col.Name === COL_NAME_APP_NAME)?.ID;
        const rows = await callServer(
            wcfclient.recordsFetch,
            new priority.netitems.form.FetchRq({
                Session,
                Properties,
                Filters: [
                    {
                        Condition: [
                            {
                                ColumnID: appNameColId,
                                Operator: priority.netitems.shared.ComparisonOperator.Eq,
                                Values: [appName]
                            }
                        ]
                    }
                ]
            }),
            ServerMethods.FetchRq
        );

        return {rows, form};
    } catch (error) {
        console.log('error', error);
        return null;
    }
};

const fetchSubFormRows = async (Session: string, keys: any) => {
    const Properties = getAdditionalProps();
    const {screenName, screenKey} = getFormData();
    const screenType = getValueByColName(COL_NAME_TYPE);
    const name = getFormName();

    try {
        await callServer(
            wcfclient.subform,
            new priority.netitems.form.SubformRq({
                Properties,
                Fname: name,
                Session,
                Keys: keys
            }),
            ServerMethods.SubformRq
        );

        const columns: priority.netitems.form.ColumnsRs = await callServer(
            wcfclient.columns,
            new priority.netitems.form.ColumnsRq({
                Session: Session,
                Properties: getAdditionalProps()
            }),
            ServerMethods.ColumnsRq
        );

        let screenTypeId, screenNameId, screenKeyId;
        columns.Columns.forEach(col => {
            if (col.Name === COL_NAME_TYPE) {
                screenTypeId = col.ID;
            } else if (col.Name === COL_NAME_SCREEN_NAME) {
                screenNameId = col.ID;
            } else if (col.Name === COL_NAME_KLINE) {
                screenKeyId = col.ID;
            }
            if (screenTypeId && screenNameId && screenKeyId) return;
        });

        return await callServer(
            wcfclient.recordsFetch,
            new priority.netitems.form.FetchRq({
                Session: Session,
                Properties: getAdditionalProps(),
                Filters: [
                    {
                        Condition: [
                            {
                                ColumnID: screenTypeId,
                                Operator: priority.netitems.shared.ComparisonOperator.Eq,
                                Values: [screenType]
                            },
                            {
                                ColumnID: screenNameId,
                                Operator: priority.netitems.shared.ComparisonOperator.Eq,
                                Values: [screenName]
                            },
                            {
                                ColumnID: screenKeyId,
                                Operator: priority.netitems.shared.ComparisonOperator.Eq,
                                Values: [screenKey]
                            }
                        ]
                    }
                ]
            }),
            ServerMethods.FetchRq
        );
    } catch (error) {
        await endForm(Session);
        return null;
    }
};

const fetchFormFields = async (Session: string, keys: any) => {
    const Properties = getAdditionalProps();

    try {
        await callServer(
            wcfclient.subform,
            new priority.netitems.form.SubformRq({
                Properties,
                Fname: 'APPFORMCOLUMNS',
                Session,
                Keys: keys
            }),
            ServerMethods.SubformRq
        );

        const columns: priority.netitems.form.ColumnsRs = await callServer(
            wcfclient.columns,
            new priority.netitems.form.ColumnsRq({
                Session: Session,
                Properties: getAdditionalProps()
            }),
            ServerMethods.ColumnsRq
        );

        let fieldPreviewColId, fieldNameColId, fieldTitleColID;
        columns.Columns.forEach(col => {
            if (col.Name === COL_NAME_APP_FIELDS_PREVIEW) {
                fieldPreviewColId = col.ID;
            } else if (col.Name === COL_NAME_APP_FIELDS_NAME) {
                fieldNameColId = col.ID;
            } else if (col.Name === COL_NAME_APP_FIELDS_TITLE) {
                fieldTitleColID = col.ID;
            }
            if (fieldPreviewColId && fieldNameColId && fieldTitleColID) return;
        });

        const fields = await callServer(
            wcfclient.recordsFetch,
            new priority.netitems.form.FetchRq({
                Session,
                Properties,
                Select: [fieldNameColId, fieldTitleColID],
                Filters: [
                    {
                        Condition: [
                            {
                                ColumnID: fieldPreviewColId,
                                Operator: priority.netitems.shared.ComparisonOperator.Eq,
                                Values: ['Y']
                            }
                        ]
                    }
                ]
            }),
            ServerMethods.FetchRq
        );

        return fields;
    } catch (error) {
        console.log('error', error);
        return null;
    }
};

export const fetchCurrentFormColumns = async (): Promise<{
    formSession: string;
    columns: priority.netitems.form.IColumn[];
}> => {
    const formName = getValueByColName(COL_NAME_SCREEN_NAME);
    const form = await startForm(formName);

    if (!form.Session) {
        throw new Error('error');
    }

    const columns = await callServer(
        wcfclient.columns,
        new priority.netitems.form.ColumnsRq({
            Session: form.Session,
            Properties: getAdditionalProps()
        }),
        ServerMethods.ColumnsRq
    );

    return {columns: columns?.Columns, formSession: form.Session};
};
